import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { Topics } from '../../../../../models/LocalNotificationTopics';
import { invalidateInfiniteCampaignReportsQuery, useInfiniteCampaignReportsQuery } from '../../../../../queries';
import { filterCancelledCampaigns } from '../../../../../utils/emailFiltering';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { LocalNotificationObserver } from '../../../../../web/components/LocalNotificationObserver';
import { Portal } from '../../../../../web/components/Portal';
import { GroupEmailTableRow } from '../../../../../web/components/reporting/emails/GroupEmailTableRow';
import { styleSheet } from './styles';

export function AdminReportingIndividualEmailTable({
	accountId,
	scrollToBottomPortalId,
	onCancelClicked,
	onRenderApprovalActions,
	onRescheduleClicked,
	onSubjectClicked,
}: {
	accountId: string;
	scrollToBottomPortalId: string;
	onCancelClicked: (_campaignToCancel?: Api.ICampaign) => (ev?: React.MouseEvent<HTMLElement>) => void;
	onRenderApprovalActions: (campaign: Api.ICampaign) => () => JSX.Element;
	onRescheduleClicked: (campaign: Api.ICampaign) => void;
	onSubjectClicked: (campaign: Api.ICampaign) => void;
}) {
	const impersonationContext = React.useMemo(() => ({ account: { id: accountId } }), [accountId]);
	const queryFilter = React.useMemo(
		() => ({
			op: Api.FilterOperator.And,
			criteria: [{ property: Api.BulkEmailFilterProperty.IndividualSends }],
		}),
		[]
	);
	const campaignReportsQuery = useInfiniteCampaignReportsQuery({
		filter: queryFilter,
		pageSize: 100,
		impersonationContext,
	});
	const invalidateQuery = () => {
		invalidateInfiniteCampaignReportsQuery({
			filter: queryFilter,
			pageSize: 100,
			impersonationContext,
		});
	};

	return (
		<>
			{campaignReportsQuery.isPending ? (
				<div className={css(styleSheet.loader)}>
					<LoadingSpinner type='large' />
				</div>
			) : null}
			{campaignReportsQuery.status === 'success' ? (
				campaignReportsQuery.data.pages[0].values.length > 0 ? (
					<>
						<div className={css(styleSheet.header)}>
							<div className={css(styleSheet.headerItem)}>Email Subject Line</div>
							<div className={css(styleSheet.headerItem)}>Sent To (Tags or Individuals)</div>
							<div className={css(styleSheet.headerItem)}>Message Analytics</div>
						</div>
						{campaignReportsQuery.data.pages.map(page => {
							// Admin section doesn't have status filter dropdown, so never show explicitly cancelled filter behavior
							const filteredCampaigns = filterCancelledCampaigns(page.values, false);

							return filteredCampaigns.map(campaign => {
								return (
									<GroupEmailTableRow
										key={campaign.id}
										onCancelClicked={onCancelClicked(campaign)}
										onRenderApprovalActions={onRenderApprovalActions(campaign)}
										onRescheduleClicked={ev => {
											ev?.preventDefault();
											ev?.stopPropagation();
											onRescheduleClicked(campaign);
										}}
										onSubjectClicked={() => onSubjectClicked(campaign)}
										styles={[styleSheet.groupEmailTableRow]}
										campaign={campaign}
									/>
								);
							});
						})}
						{campaignReportsQuery.hasNextPage && !campaignReportsQuery.isFetchingNextPage ? (
							<Portal destination={scrollToBottomPortalId}>
								<Waypoint bottomOffset='-200px' onEnter={() => campaignReportsQuery.fetchNextPage()} />
							</Portal>
						) : null}
					</>
				) : (
					<div className={css(styleSheet.noneFound)}>No individual emails found</div>
				)
			) : null}
			<LocalNotificationObserver
				topic={Topics.CREATE_CAMPAIGNS_ITEM}
				onNotificationReceived={() => invalidateQuery()}
			/>
		</>
	);
}
